import { Event } from '../lib/supabase';
import { Check, Calendar, MapPin, User } from 'lucide-react';

interface EventCardProps {
  event: Event & {
    venue?: { name: string } | null;
    creator?: { username: string } | null;
    responses?: Array<{ user_id: string; response_type: string }>;
  };
  currentUserId?: string;
  onClick?: () => void;
}

export default function EventCard({ event, currentUserId, onClick }: EventCardProps) {
  const userResponse = event.responses?.find(r => r.user_id === currentUserId);
  const isAttending = userResponse?.response_type === 'attending';
  const isInterested = userResponse?.response_type === 'interested';

  // Format date and time
  const eventDate = new Date(event.start_datetime);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '8px',
        border: '1px solid var(--color-border)',
        cursor: onClick ? 'pointer' : 'default',
        // Background image (only when event has a photo)
        ...(event.photo_url && {
          backgroundImage: `url(${event.photo_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }),
        backgroundColor: event.photo_url ? 'transparent' : 'var(--color-card)',
      }}
    >
      {/* Gradient overlay - positioned absolutely to cover entire container including edges */}
      {event.photo_url && (
        <>
          {/* Dark gradient overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(11, 11, 11, 0.95) 0%, rgba(11, 11, 11, 0.80) 50%, rgba(11, 11, 11, 0.25) 80%, transparent 100%)',
            pointerEvents: 'none',
          }} />
          
          {/* Blur overlay - fades from left to right */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(3px)',
            WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 70%)',
            maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 70%)',
            pointerEvents: 'none',
          }} />
        </>
      )}

      {/* Content - positioned relatively above gradient */}
      <div style={{ 
        position: 'relative', 
        padding: '1rem',
        zIndex: 1 
      }}>
        {/* Header: Title and Status Badge */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '0.5rem',
          gap: '0.75rem'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: 'var(--color-secondary)',
            flex: 1,
            margin: 0
          }}>
            {event.title}
          </h4>
          
          {/* Status badges */}
          {isAttending && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '28px',
              height: '28px',
              backgroundColor: 'var(--color-success)',
              borderRadius: '50%',
              color: '#ffffff',
              flexShrink: 0
            }}>
              <Check size={18} />
            </div>
          )}
          {isInterested && !isAttending && (
            <div style={{
              padding: '0.25rem 0.5rem',
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
              border: '1px solid rgba(255, 165, 0, 0.5)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#ffa500',
              fontWeight: '500',
              flexShrink: 0
            }}>
              Interested
            </div>
          )}
        </div>

        {/* Venue */}
        {event.venue?.name && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--color-secondary)'
          }}>
            <MapPin size={14} />
            <span>{event.venue.name}</span>
          </div>
        )}

        {/* Date and Time */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          marginBottom: '0.5rem',
          fontSize: '0.875rem',
          color: 'var(--color-secondary)'
        }}>
          <Calendar size={14} />
          <span>{formattedDate} at {formattedTime}</span>
        </div>

        {/* Creator */}
        {event.creator?.username && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--color-secondary)'
          }}>
            <User size={12} />
            <span>by {event.creator.username}</span>
          </div>
        )}
      </div>
    </div>
  );
}

